import update from "immutability-helper";
import * as moment from "moment";
import React from "react";
import { connect } from "react-redux";
import { Message } from "semantic-ui-react";

import { FilterDatePicker } from "components/DatePicker/SingleDatePicker";
import { FormRow, TimePicker } from "../../../components";
import { fetchGuestlist, updateGuestlist } from "../../../redux/actionCreators/guestlist";
import { type ISettingsForm } from "./models";

import { selector } from "../../";

import { RadioGroup } from "components/Atoms/inputs/RadioGroup";
import { type ApiError } from "../../model";

interface IPropsFromState {
  guestlist: server.dto.LegacyGuestlist
  settingErrors: ApiError[]
  isLoading: boolean
}

interface IPropsFromDispatch {
  refreshGuestlist: (showId: number) => any
  updateGuestlist: (guestlist: server.dto.LegacyGuestlist) => any
}

const mapStateToProps = (state): IPropsFromState => ({
  guestlist: selector.getGuestlist(state),
  isLoading: selector.getGuestlistSettingsIsLoading(state),
  settingErrors: selector.getGuestlistSettingsErrors(state)
});

const mapDispatchToProps = (dispatch): IPropsFromDispatch => ({
  refreshGuestlist: (showId: number) => dispatch(fetchGuestlist(showId)),
  updateGuestlist: (guestlist: server.dto.LegacyGuestlist) => dispatch(updateGuestlist(guestlist))
});

class SettingsForm extends React.Component<IPropsFromState & IPropsFromDispatch, ISettingsForm> {
  // Checks if a string is valid ISettingsForm property name
  // Provides extra compile time safety when it comes to forms
  check = (name: keyof ISettingsForm) => name;

  constructor(props) {
    super(props);
    this.state = this.getInitialValues();
  }

  private readonly isClosed = (): boolean => {
    const { guestlist } = this.props;
    return moment(guestlist.deadlineUtc) < moment().utc();
  };

  private readonly closed = (): string => this.isClosed() ? RESX.Guestlist.ClosedStatus : RESX.Guestlist.OpenStatus;

  private readonly getInitialValues = (): ISettingsForm => {
    const { guestlist } = this.props;
    const datetime = moment.utc(guestlist.deadlineUtc);

    return {
      emailMandatory: guestlist.emailMandatory,
      endDate: datetime,
      endTime: datetime,
      multipleGuestsPerName: guestlist.multipleGuestsPerName,
      passportRequired: guestlist.passportRequired,
      phoneMandatory: guestlist.phoneMandatory
    };
  };

  private readonly combineDateTime = (): Date => {
    const endDate = this.state.endDate.clone().startOf("day");
    const endTime = this.state.endTime.clone().utc();
    endDate.set("hour", endTime.hour());
    endDate.set("minute", endTime.minute());
    return endDate.toDate();
  };

  private readonly handleSubmit = () => {
    const { emailMandatory, multipleGuestsPerName, phoneMandatory, passportRequired } = this.state;
    const newGuestlist = update(this.props.guestlist, {
      deadlineUtc: { $set: this.combineDateTime() },
      emailMandatory: { $set: emailMandatory },
      multipleGuestsPerName: { $set: multipleGuestsPerName },
      passportRequired: { $set: passportRequired },
      phoneMandatory: { $set: phoneMandatory }
    });

    this.props.updateGuestlist(newGuestlist)
      .then(() => this.props.refreshGuestlist(this.props.guestlist.showId));
  };

  private setEmailMandatory = (emailMandatory: boolean) => {
    if (this.props.isLoading) {
      return;
    }

    this.setState(state => ({ ...state, emailMandatory }), this.handleSubmit);
  }

  private setPhoneMandatory = (phoneMandatory: boolean) => {
    if (this.props.isLoading) {
      return;
    }

    this.setState(state => ({ ...state, phoneMandatory }), this.handleSubmit);
  }

  private setPassportRequired = (passportRequired: boolean) => {
    if (this.props.isLoading) {
      return;
    }

    this.setState(state => ({ ...state, passportRequired }), this.handleSubmit);
  }

  private setMultipleGuestsPerName = (multipleGuestsPerName: boolean) => {
    if (this.props.isLoading) {
      return;
    }

    this.setState(state => ({ ...state, multipleGuestsPerName }), this.handleSubmit);
  }

  private readonly onTimeChange = (time) => {
    if (this.props.isLoading) {
      return;
    }
    this.setState({ endTime: moment(time) }, this.handleSubmit);
  };

  private readonly onDateChange = (date) => {
    if (this.props.isLoading) {
      return;
    }
    this.setState({ endDate: date }, this.handleSubmit);
  };

  render() {
    const { endDate, endTime, emailMandatory, passportRequired, phoneMandatory, multipleGuestsPerName } = this.state;

    return (
      <form>
        <div className="flex-column gap">
          <FormRow labelWidth={120} label={RESX.Status.resxStatus}>
            {this.closed()}
          </FormRow>
          <FormRow labelWidth={120} label={RESX.Guestlist.ClosingDate}>
            <div className="flex gap-small">
              <FilterDatePicker
                dontUpdateOnInputChange
                minDate={moment(serverReference.constants.appointment.minDate)}
                maxDate={moment(serverReference.constants.appointment.maxDate)}
                value={endDate}
                onChange={this.onDateChange}
              />
              <TimePicker value={endTime} onChange={this.onTimeChange} />
            </div>
          </FormRow>
          <FormRow labelWidth={120} label={RESX.GeneralLabels.Email}>
            <RadioGroup
              orientation="horizontal"
              options={[
                { label: RESX.Advancing.Required, value: "true" },
                { label: RESX.Advancing.Optional, value: "false" }
              ]}
              value={emailMandatory ? "true" : "false"}
              onChange={(value) => this.setEmailMandatory(value === "true")}
            />
          </FormRow>
          <FormRow labelWidth={120} label={RESX.Contact.Mobile}>
            <RadioGroup
              orientation="horizontal"
              options={[
                { label: RESX.Advancing.Required, value: "true" },
                { label: RESX.Advancing.Optional, value: "false" }
              ]}
              value={phoneMandatory ? "true" : "false"}
              onChange={(value) => this.setPhoneMandatory(value === "true")}
            />
          </FormRow>
          <FormRow labelWidth={120} label={RESX.Guestlist.Passport}>
            <RadioGroup
              orientation="horizontal"
              options={[
                { label: RESX.Advancing.Required, value: "true" },
                { label: RESX.Advancing.Optional, value: "false" }
              ]}
              value={passportRequired ? "true" : "false"}
              onChange={(value) => this.setPassportRequired(value === "true")}
            />
          </FormRow>
          <FormRow labelWidth={120} label={RESX.Guestlist.MultiplePerNameLbl}>
            <RadioGroup
              orientation="horizontal"
              options={[
                { label: RESX.GeneralLabels.lblYes, value: "true" },
                { label: RESX.GeneralLabels.lblNo, value: "false" }
              ]}
              value={multipleGuestsPerName ? "true" : "false"}
              onChange={(value) => this.setMultipleGuestsPerName(value === "true")}
            />
          </FormRow>

          {this.props.settingErrors.length > 0 && <div>
            <Message color="red">
              {this.props.settingErrors.map((error) => <p key={error.code}>{error.message}</p>)}
            </Message>
          </div>}
        </div>
      </form>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(SettingsForm);
